import { calculateChessProbabilities } from "./chessProbability";
import { buildSwissRound } from "./chessSwissPairing";

export const CHESS_RESULTS = ["1-0", "1/2-1/2", "0-1"];
export const CHESS_FORFEIT_SOURCE = "forfeit";
export const chessResultInputValue = (game) => game?.resultSource === CHESS_FORFEIT_SOURCE
  ? `${game.result}-forfeit` : game?.result || "";
export const parseChessResultInput = (value) => ({
  result: String(value).replace(/-forfeit$/, "") || null,
  resultSource: String(value).endsWith("-forfeit") ? CHESS_FORFEIT_SOURCE : value ? "manual" : null,
});
export const isEloRatedChessGame = (game) => Boolean(
  game && !game.bye && game.completed && CHESS_RESULTS.includes(game.result)
  && game.resultSource !== CHESS_FORFEIT_SOURCE,
);
export function validateChessRound(games = []) {
  const players = new Set();
  const boards = new Set();
  const errors = [];
  games.forEach((game, index) => {
    const board = Number(game.boardNumber ?? index + 1);
    if (!Number.isInteger(board) || board < 1 || boards.has(board)) errors.push("board");
    boards.add(board);
    const participants = game.bye
      ? [game.whitePlayerId]
      : [game.whitePlayerId, game.blackPlayerId];
    if (participants.some((id) => id === null || id === undefined || id === "")) errors.push("missing");
    if (!game.bye && game.whitePlayerId != null && game.blackPlayerId != null
      && game.whitePlayerId === game.blackPlayerId) errors.push("same");
    participants.filter((id) => id !== null && id !== undefined && id !== "").forEach((id) => {
      if (players.has(id)) errors.push("duplicate");
      players.add(id);
    });
  });
  return [...new Set(errors)];
}
export const effectiveMinutesFromTimeControl = (baseMinutes, incrementSeconds) => (
  (Number(baseMinutes) || 0) + (Number(incrementSeconds) || 0)
);
export const fairOdd = (probability) => (probability > 0 ? 1 / probability : null);

export function createChessGame({ id, round, whitePlayerId, blackPlayerId, bye = false }) {
  return {
    id, round, whitePlayerId, blackPlayerId, result: bye ? "bye" : null,
    resultSource: bye ? "simulated" : null, played: bye, completed: bye,
    probabilities: null, bye,
  };
}

export function buildRoundRobinSchedule(players) {
  if (!Array.isArray(players) || players.length < 2) return [];
  const rotation = [...players.map(({ id }) => id)];
  if (rotation.length % 2 === 1) rotation.push(null);
  const rounds = [];
  const colorBalance = new Map(players.map(({ id }) => [id, 0]));

  for (let roundIndex = 0; roundIndex < rotation.length - 1; roundIndex += 1) {
    const games = [];
    for (let boardIndex = 0; boardIndex < rotation.length / 2; boardIndex += 1) {
      const first = rotation[boardIndex];
      const second = rotation[rotation.length - 1 - boardIndex];
      if (first === null || second === null) continue;
      const firstWhiteCost = Math.abs(colorBalance.get(first) + 1)
        + Math.abs(colorBalance.get(second) - 1);
      const secondWhiteCost = Math.abs(colorBalance.get(first) - 1)
        + Math.abs(colorBalance.get(second) + 1);
      const reverseColors = secondWhiteCost < firstWhiteCost
        || (secondWhiteCost === firstWhiteCost && (roundIndex + boardIndex) % 2 === 1);
      const whiteId = reverseColors ? second : first;
      const blackId = reverseColors ? first : second;
      games.push(createChessGame({
        id: `round-robin-${roundIndex + 1}-${boardIndex + 1}`,
        round: roundIndex + 1,
        whitePlayerId: whiteId,
        blackPlayerId: blackId,
      }));
      colorBalance.set(whiteId, colorBalance.get(whiteId) + 1);
      colorBalance.set(blackId, colorBalance.get(blackId) - 1);
    }
    rounds.push(games);
    rotation.splice(1, 0, rotation.pop());
  }

  return rounds;
}

export function buildGameProbabilities({ game, playersById, effectiveMinutes, parameters }) {
  const white = playersById.get(game.whitePlayerId);
  const black = playersById.get(game.blackPlayerId);
  return calculateChessProbabilities({
    player1Elo: white.elo,
    player2Elo: black.elo,
    player1Color: "white",
    effectiveMinutes,
    player1DrawFactor: white.drawFactor,
    player2DrawFactor: black.drawFactor,
    parameters,
  });
}

export function buildMatchSchedule(players, gameCount) {
  if (!Array.isArray(players) || players.length !== 2) return [];
  return Array.from({ length: Math.max(1, Math.floor(Number(gameCount) || 1)) }, (_, index) => createChessGame({
    id: `match-${index + 1}`,
    round: index + 1,
    whitePlayerId: players[index % 2].id,
    blackPlayerId: players[(index + 1) % 2].id,
  }));
}

export function pointsFromGames(players, games) {
  const points = new Map(players.map(({ id }) => [id, 0]));
  games.forEach((game) => {
    if (game.bye) points.set(game.whitePlayerId, points.get(game.whitePlayerId) + 1);
    else if (game.result === "1-0") points.set(game.whitePlayerId, points.get(game.whitePlayerId) + 1);
    else if (game.result === "0-1") points.set(game.blackPlayerId, points.get(game.blackPlayerId) + 1);
    else if (game.result === "1/2-1/2") {
      points.set(game.whitePlayerId, points.get(game.whitePlayerId) + 0.5);
      points.set(game.blackPlayerId, points.get(game.blackPlayerId) + 0.5);
    }
  });
  return points;
}

const shuffle = (entries, rng) => {
  const result = [...entries];
  for (let index = result.length - 1; index > 0; index -= 1) {
    const target = Math.min(index, Math.floor(rng() * (index + 1)));
    [result[index], result[target]] = [result[target], result[index]];
  }
  return result;
};

const directResultPoints = (game, playerId) => {
  if (game.result === "1/2-1/2") return 0.5;
  if (game.result === "1-0") return game.whitePlayerId === playerId ? 1 : 0;
  if (game.result === "0-1") return game.blackPlayerId === playerId ? 1 : 0;
  return 0;
};

const resolveSwissTieGroup = (group, games, rng) => {
  if (group.length < 2) return group;
  const ids = new Set(group.map(({ id }) => id));
  const directGames = games.filter((game) => (
    !game.bye
    && ids.has(game.whitePlayerId)
    && ids.has(game.blackPlayerId)
    && CHESS_RESULTS.includes(game.result)
  ));
  const requiredPairs = group.length * (group.length - 1) / 2;
  const playedPairs = new Set(directGames.map(({ whitePlayerId, blackPlayerId }) => (
    [String(whitePlayerId), String(blackPlayerId)].sort().join(":")
  )));
  if (playedPairs.size !== requiredPairs) return shuffle(group, rng);

  const directPoints = new Map(group.map(({ id }) => [id, 0]));
  directGames.forEach((game) => {
    directPoints.set(game.whitePlayerId, directPoints.get(game.whitePlayerId) + directResultPoints(game, game.whitePlayerId));
    directPoints.set(game.blackPlayerId, directPoints.get(game.blackPlayerId) + directResultPoints(game, game.blackPlayerId));
  });
  const ordered = group.map((player) => ({ ...player, directPoints: directPoints.get(player.id) }))
    .sort((first, second) => second.directPoints - first.directPoints);
  const resolved = [];
  for (let start = 0; start < ordered.length;) {
    let end = start + 1;
    while (end < ordered.length && ordered[end].directPoints === ordered[start].directPoints) end += 1;
    resolved.push(...shuffle(ordered.slice(start, end), rng));
    start = end;
  }
  return resolved;
};

export function buildSwissStandings(players, games, rng = Math.random) {
  const points = pointsFromGames(players, games);
  const opponents = new Map(players.map(({ id }) => [id, []]));
  games.filter((game) => (
    !game.bye
    && game.whitePlayerId != null
    && game.blackPlayerId != null
    && ["1-0", "0-1", "1/2-1/2"].includes(game.result)
  )).forEach((game) => {
    opponents.get(game.whitePlayerId)?.push(game.blackPlayerId);
    opponents.get(game.blackPlayerId)?.push(game.whitePlayerId);
  });
  const buchholz = new Map(players.map(({ id }) => [id,
    (opponents.get(id) || []).reduce((sum, opponentId) => sum + (points.get(opponentId) || 0), 0),
  ]));
  const refinedBuchholz = new Map(players.map(({ id }) => [id,
    (opponents.get(id) || []).reduce((sum, opponentId) => sum + (buchholz.get(opponentId) || 0), 0),
  ]));
  const standings = players.map((player) => ({
    ...player,
    points: points.get(player.id) || 0,
    buchholz: buchholz.get(player.id) || 0,
    refinedBuchholz: refinedBuchholz.get(player.id) || 0,
  })).sort((first, second) => (
    second.points - first.points
    || second.buchholz - first.buchholz
    || second.refinedBuchholz - first.refinedBuchholz
  ));
  const resolved = [];
  for (let start = 0; start < standings.length;) {
    let end = start + 1;
    while (end < standings.length
      && standings[end].points === standings[start].points
      && standings[end].buchholz === standings[start].buchholz
      && standings[end].refinedBuchholz === standings[start].refinedBuchholz) end += 1;
    resolved.push(...resolveSwissTieGroup(standings.slice(start, end), games, rng));
    start = end;
  }
  return resolved.map((player, index) => ({ ...player, place: index + 1 }));
}

const sampleResult = (probabilities, rng) => {
  const random = rng();
  if (random < probabilities.player1Win) return "1-0";
  if (random < probabilities.player1Win + probabilities.draw) return "1/2-1/2";
  return "0-1";
};

export const prepareSimulationBaseline = (games = []) => games.map((game) => {
  if (game.bye || (game.completed && game.result && game.resultSource !== "simulated")) {
    return { ...game };
  }
  return {
    ...game,
    result: null,
    resultSource: null,
    played: false,
    completed: false,
  };
});

const reopenGame = (game) => game.bye ? { ...game } : {
  ...game, result: null, resultSource: null, played: false, completed: false,
};

export function buildTournamentSimulationPlan({
  tournamentType, games = [], scope = "current", throughRound = 0, configuredRounds = 1,
}) {
  const maximumExistingRound = Math.max(0, ...games.map(({ round }) => Number(round) || 0));
  const firstOpenRound = [...new Set(games.filter((game) => !game.completed).map(({ round }) => Number(round)))]
    .sort((first, second) => first - second)[0];
  const nextRound = firstOpenRound || maximumExistingRound + 1 || 1;
  const roundLimit = Math.max(1, Math.floor(Number(configuredRounds) || 1));

  if (scope === "whole") {
    return {
      games: tournamentType === "swiss" ? [] : games.map(reopenGame),
      targetRounds: roundLimit,
      startLabel: "whole",
    };
  }
  if (scope === "round") {
    const fixedThrough = Math.max(0, Math.floor(Number(throughRound) || 0));
    return {
      games: tournamentType === "swiss"
        ? games.filter(({ round }) => Number(round) <= fixedThrough)
        : games.map((game) => Number(game.round) <= fixedThrough ? { ...game } : reopenGame(game)),
      targetRounds: roundLimit,
      startLabel: "round",
      throughRound: fixedThrough,
    };
  }
  if (scope === "next") {
    return {
      games: games.filter(({ round }) => Number(round) <= nextRound),
      targetRounds: Math.min(roundLimit, nextRound),
      startLabel: "next",
      nextRound,
    };
  }
  return {
    games: [...games],
    targetRounds: roundLimit,
    startLabel: "current",
    throughRound: maximumExistingRound,
  };
}

export function playFixedSchedule({ players, schedule, effectiveMinutes, parameters, rng = Math.random, resultOverride }) {
  const playersById = new Map(players.map((player) => [player.id, player]));
  return schedule.map((game) => {
    if (game.completed && game.result) return { ...game };
    if (game.bye) return { ...game, result: "bye", resultSource: game.resultSource || "simulated", played: true, completed: true };
    const probabilities = game.probabilities
      || buildGameProbabilities({ game, playersById, effectiveMinutes, parameters });
    const overriddenResult = resultOverride?.(game, probabilities, playersById);
    return {
      ...game, probabilities, result: overriddenResult || sampleResult(probabilities, rng),
      resultSource: "simulated", played: true, completed: true,
    };
  });
}

export function createTournamentAccumulator(players) {
  return new Map(players.map(({ id }) => [id, {
    points: 0,
    ranks: 0,
    firsts: 0,
    topThrees: 0,
    placements: Array(players.length).fill(0),
    pointCounts: {},
    minimumPoints: Infinity,
    maximumPoints: -Infinity,
  }]));
}

const shuffleTies = (standings, rng) => {
  const ordered = [...standings].sort((a, b) => b.points - a.points);
  for (let start = 0; start < ordered.length;) {
    let end = start + 1;
    while (end < ordered.length && ordered[end].points === ordered[start].points) end += 1;
    for (let index = end - 1; index > start; index -= 1) {
      const target = start + Math.floor(rng() * (index - start + 1));
      [ordered[index], ordered[target]] = [ordered[target], ordered[index]];
    }
    start = end;
  }
  return ordered;
};

export function accumulateTournamentResult(players, games, accumulator, rng = Math.random, ranking = "random") {
  const points = pointsFromGames(players, games);
  const standings = ranking === "swiss"
    ? buildSwissStandings(players, games, rng)
    : shuffleTies(players.map(({ id }) => ({ id, points: points.get(id) })), rng);
  standings.forEach(({ id, points: playerPoints }, index) => {
    const aggregate = accumulator.get(id);
    aggregate.points += playerPoints;
    aggregate.ranks += index + 1;
    if (index === 0) aggregate.firsts += 1;
    if (index < 3) aggregate.topThrees += 1;
    aggregate.placements[index] += 1;
    const pointKey = String(playerPoints);
    aggregate.pointCounts[pointKey] = (aggregate.pointCounts[pointKey] || 0) + 1;
    aggregate.minimumPoints = Math.min(aggregate.minimumPoints, playerPoints);
    aggregate.maximumPoints = Math.max(aggregate.maximumPoints, playerPoints);
  });
  return standings;
}

export function simulateTournamentBatch({
  players,
  schedule,
  effectiveMinutes,
  parameters,
  iterations,
  accumulator = createTournamentAccumulator(players),
  rng = Math.random,
}) {
  const playersById = new Map(players.map((player) => [player.id, player]));
  const games = prepareSimulationBaseline(schedule.flat()).map((game) => (game.completed || game.bye ? game : {
    ...game,
    probabilities: buildGameProbabilities({ game, playersById, effectiveMinutes, parameters }),
  }));

  for (let simulation = 0; simulation < iterations; simulation += 1) {
    const playedGames = playFixedSchedule({ players, schedule: games, effectiveMinutes, parameters, rng });
    accumulateTournamentResult(players, playedGames, accumulator, rng);
  }
  return accumulator;
}

export const buildSwissPlayerState = (players, games) => {
  const points = pointsFromGames(players, games);
  return players.map((player, index) => ({
    ...player,
    seed: index + 1,
    points: points.get(player.id),
    hadBye: games.some((game) => game.bye && game.whitePlayerId === player.id),
    colors: games.filter((game) => !game.bye && (game.whitePlayerId === player.id || game.blackPlayerId === player.id))
      .map((game) => (game.whitePlayerId === player.id ? "white" : "black")),
  }));
};

export function playSwissTournament({ players, rounds, existingGames = [], effectiveMinutes, parameters, rng = Math.random, resultOverride }) {
  let games = prepareSimulationBaseline(existingGames);
  for (let round = 1; round <= rounds; round += 1) {
    const existingRound = games.filter((game) => game.round === round);
    if (existingRound.length) {
      const played = playFixedSchedule({ players, schedule: existingRound, effectiveMinutes, parameters, rng, resultOverride });
      games = [...games.filter((game) => game.round !== round), ...played];
    } else {
      const state = buildSwissPlayerState(players, games);
      const paired = buildSwissRound(state, { round, previousGames: games });
      games.push(...playFixedSchedule({ players, schedule: paired, effectiveMinutes, parameters, rng, resultOverride }));
    }
  }
  return games.sort((a, b) => a.round - b.round || String(a.id).localeCompare(String(b.id)));
}

export function simulateSwissTournamentBatch({
  players, rounds, existingGames = [], effectiveMinutes, parameters, iterations,
  accumulator = createTournamentAccumulator(players), rng = Math.random, resultOverride,
}) {
  for (let index = 0; index < iterations; index += 1) {
    const games = playSwissTournament({ players, rounds, existingGames, effectiveMinutes, parameters, rng, resultOverride });
    accumulateTournamentResult(players, games, accumulator, rng, "swiss");
  }
  return accumulator;
}

export function simulateMatchTournament({ players, gameCount, existingGames = [], effectiveMinutes, parameters, iterations, rng = Math.random }) {
  const baseSchedule = prepareSimulationBaseline(buildMatchSchedule(players, gameCount).map((game) => {
    const existing = existingGames.find(({ id }) => id === game.id);
    return existing || game;
  }));
  const playersById = new Map(players.map((player) => [player.id, player]));
  const preparedSchedule = baseSchedule.map((game) => (game.completed ? game : {
    ...game,
    probabilities: buildGameProbabilities({ game, playersById, effectiveMinutes, parameters }),
  }));
  const summary = { player1Wins: 0, draws: 0, player2Wins: 0, player1Points: 0, player2Points: 0, scoreDistribution: {} };
  for (let index = 0; index < iterations; index += 1) {
    const games = playFixedSchedule({ players, schedule: preparedSchedule, effectiveMinutes, parameters, rng });
    const points = pointsFromGames(players, games);
    const first = points.get(players[0].id);
    const second = points.get(players[1].id);
    summary.player1Points += first;
    summary.player2Points += second;
    if (first > second) summary.player1Wins += 1;
    else if (second > first) summary.player2Wins += 1;
    else summary.draws += 1;
    const score = `${first}-${second}`;
    summary.scoreDistribution[score] = (summary.scoreDistribution[score] || 0) + 1;
  }
  return {
    ...summary,
    player1WinProbability: summary.player1Wins / iterations,
    drawProbability: summary.draws / iterations,
    player2WinProbability: summary.player2Wins / iterations,
    averagePlayer1Points: summary.player1Points / iterations,
    averagePlayer2Points: summary.player2Points / iterations,
  };
}

export function finalizeTournamentResults(players, accumulator, iterations) {
  return players.map((player) => {
    const aggregate = accumulator.get(player.id);
    return {
      id: player.id,
      name: player.name,
      averagePoints: aggregate.points / iterations,
      averageRank: aggregate.ranks / iterations,
      firstProbability: aggregate.firsts / iterations,
      topThreeProbability: players.length >= 3 ? aggregate.topThrees / iterations : null,
      placementProbabilities: aggregate.placements.map((count) => count / iterations),
      placementCounts: [...aggregate.placements],
      topNProbabilities: aggregate.placements.map((_, index) => (
        aggregate.placements.slice(0, index + 1).reduce((sum, count) => sum + count, 0) / iterations
      )),
      bottomNProbabilities: aggregate.placements.map((_, index) => (
        aggregate.placements.slice(index).reduce((sum, count) => sum + count, 0) / iterations
      )),
      pointDistribution: Object.fromEntries(Object.entries(aggregate.pointCounts)
        .map(([points, count]) => [points, { count, probability: count / iterations }])),
      minimumPoints: aggregate.minimumPoints,
      maximumPoints: aggregate.maximumPoints,
    };
  }).sort((a, b) => a.averageRank - b.averageRank);
}

export function buildTournamentHistory(players, games, tournamentType = "roundRobin") {
  const maximumRound = Math.max(0, ...games.map(({ round }) => Number(round) || 0));
  return Array.from({ length: maximumRound }, (_, index) => {
    const round = index + 1;
    const gamesThroughRound = games.filter((game) => game.round <= round);
    const points = pointsFromGames(players, gamesThroughRound);
    const standings = tournamentType === "swiss"
      ? buildSwissStandings(players, gamesThroughRound)
      : players.map((player) => ({ ...player, points: points.get(player.id) }))
        .sort((a, b) => b.points - a.points || b.elo - a.elo);
    return {
      round,
      games: games.filter((game) => game.round === round),
      standings,
    };
  });
}

export function resultForPlayer(game, playerId) {
  if (game.bye && game.whitePlayerId === playerId) return "1";
  if (game.result === "1/2-1/2") return "½";
  if (game.whitePlayerId === playerId) return game.result === "1-0" ? "1" : game.result === "0-1" ? "0" : "–";
  if (game.blackPlayerId === playerId) return game.result === "0-1" ? "1" : game.result === "1-0" ? "0" : "–";
  return "–";
}

export function playerTournamentSummary(playerId, games) {
  const playerGames = games.filter((game) => game.whitePlayerId === playerId || game.blackPlayerId === playerId)
    .sort((a, b) => a.round - b.round);
  const chain = playerGames.map((game) => resultForPlayer(game, playerId));
  return {
    games: playerGames,
    chain,
    played: chain.filter((result) => result !== "–").length,
    wins: chain.filter((result) => result === "1").length,
    draws: chain.filter((result) => result === "½").length,
    losses: chain.filter((result) => result === "0").length,
  };
}

export function simulateRoundRobinTournament({ players, effectiveMinutes, parameters, iterations, rng = Math.random }) {
  const schedule = buildRoundRobinSchedule(players);
  const accumulator = simulateTournamentBatch({
    players, schedule, effectiveMinutes, parameters, iterations, rng,
  });
  return finalizeTournamentResults(players, accumulator, iterations);
}
